import React, { useEffect, useState, useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  getAdminProfile,
  updateAdminProfile,
  uploadSingleFile,
} from "../Services/userService";
import { toast } from "react-toastify";
import Skeleton from "../Components/SkeletonLoader";

export default function Profile() {
  const [profileData, setProfileData] = useState({
    fullName: "",
    email: "",
    dob: "",
    profileImage: "",
    role: "",
    contactPersonName: "",
    companyName: "",
    businessRegistrationNumber: "",
    phone: "",
    headquartersAddress: "",
    serviceTypes:[]
  });
  const [previewImage, setPreviewImage] = useState(null);
  const [sendPreviewImage, setSendPreviewImage] = useState();
  const [loading, setLoading] = useState(true);
  const [dobDate, setDobDate] = useState(null);
  const [saving, setSaving] = useState(false);

  const fileInputRef = useRef(null);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const profileResp = await getAdminProfile();
      const adminData =
        profileResp.data?.data?.admin || profileResp?.data?.data?.user;
      const role = profileResp.data.data?.role;
      const firstAdmin = Array.isArray(adminData) ? adminData[0] : adminData;
      if (firstAdmin) {
        setProfileData({
          fullName: firstAdmin?.fullName || "",
          email: firstAdmin?.email || "",
          dob: firstAdmin?.dob || "",
          profileImage: firstAdmin?.profileImage || "",
          role: role || "",
          contactPersonName: firstAdmin?.contactPersonName || "",
          companyName: firstAdmin?.companyName || "",
          businessRegistrationNumber: firstAdmin?.businessRegistrationNumber || "",
          phone: firstAdmin?.phone || "",
          headquartersAddress: firstAdmin?.headquartersAddress?.address || "",
          serviceTypes: firstAdmin?.serviceTypes || [],
        });

        if (firstAdmin.profileImage) {
          setPreviewImage(firstAdmin.profileImage);
        }

        if (firstAdmin.dob) {
          setDobDate(new Date(firstAdmin.dob));
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDateChange = (date) => {
    setDobDate(date);
    setProfileData((prev) => ({
      ...prev,
      dob: date ? date.toISOString() : "",
    }));
  };

  const handleImageClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith("image/")) {
      const reader = new FileReader();

      const fileAsBase64 = await new Promise((resolve, reject) => {
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => reject("Failed to read file");
        reader.readAsDataURL(file);
      });
      const payload = { base64: fileAsBase64 };
      const response = await uploadSingleFile(payload);
      setPreviewImage(response.data.data.fileName);
      if (response?.status == 200) {
        setSendPreviewImage(response.data.data.fileName);
      } else {
        console.error("Upload failed or fileName not found in response");
      }
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...profileData,
        profileImage: sendPreviewImage,
      };
      const res = await updateAdminProfile(payload);
      toast.success("Profile updated successfully!");
    } catch (err) {
      console.error("Update failed:", err);
      toast.error("Error updating profile.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-fluid">
      <div className="row profileOuterSection">
        <div className="col-md-8">
          <div className="profileShadeSection">
            <div className="userProfileHeader">
              <div
                className="userProfile"
                onClick={handleImageClick}
                style={{ cursor: "pointer" }}
              >
                {loading ? (
                  <Skeleton circle width={80} height={80} />
                ) : (
                  <img
                    src={
                      previewImage
                        ? `${process.env.REACT_APP_IMAGE_URL}/${previewImage}`
                        : "/assets/img/download.png"
                    }
                    alt="user"
                  />
                )}
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  ref={fileInputRef}
                  onChange={handleFileChange}
                />
              </div>

              <div className="userProfileDetails">
                {loading ? (
                  <>
                    <Skeleton width={200} height={20} />
                    <Skeleton width={250} height={16} />
                  </>
                ) : (
                  <>
                    <h6 className="m-0">
                      <span>
                        {profileData.fullName ||
                          profileData?.contactPersonName ||
                          "N/A"}
                      </span>{" "}
                      /{" "}
                      <span>
                        {profileData?.role === "admin" ? "Admin" : "Vendor"}
                      </span>
                    </h6>
                    <p className="m-0">
                      Update your username and manage your account
                    </p>
                    {profileData.role == "partner" && (
                      <p className="m-0 servicesType">
                        {profileData?.serviceTypes?.map((service, index) => (
                          <span key={index}>
                            {service}
                            {index !== profileData.serviceTypes.length - 1 && ", "}
                          </span>
                        ))}
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>

            {profileData.role == "partner" && (
              <div className="profileOutRowSec">
                <div className="userInformation">
                  <p className="m-0">Company name</p>
                  {loading ? (
                    <Skeleton width="100%" height={40} />
                  ) : (
                    <input
                      type="text"
                      name="companyName"
                      placeholder="Enter company name"
                      value={profileData.companyName}
                      onChange={handleChange}
                    />
                  )}
                </div>

                <div className="userInformation">
                  <p className="m-0">Business registration number / Tax id</p>
                  {loading ? (
                    <Skeleton width="100%" height={40} />
                  ) : (
                    <input
                      type="text"
                      name="businessRegistrationNumber"
                      placeholder="Enter business registration number / Tax id"
                      value={profileData.businessRegistrationNumber}
                      disabled
                    />
                  )}
                </div>
              </div>
            )}

            {profileData.role == "partner" && (
              <div className="profileOutRowSec">
                <div className="userInformation">
                  <p className="m-0">Phone no</p>
                  {loading ? (
                    <Skeleton width="100%" height={40} />
                  ) : (
                    <input
                      type="tel"
                      name="phone"
                      placeholder="Enter phone number"
                      value={profileData.phone}
                      onChange={handleChange}
                    />
                  )}
                </div>

                <div className="userInformation">
                  <p className="m-0">Headquarters address</p>
                  {loading ? (
                    <Skeleton width="100%" height={40} />
                  ) : (
                    <input
                      type="text"
                      name="headquartersAddress"
                      placeholder="Enter headquarters address"
                      value={profileData?.headquartersAddress}
                    />
                  )}
                </div>
              </div>
            )}


            <div className="profileOutRowSec">
              <div className="userInformation">
                <p className="m-0">Username</p>
                {loading ? (
                  <Skeleton width="100%" height={40} />
                ) : (
                  <input
                    type="text"
                    name={
                      profileData.role === "admin"
                        ? "fullName"
                        : "contactPersonName"
                    }
                    placeholder="Enter username"
                    value={
                      profileData.role === "admin"
                        ? profileData.fullName
                        : profileData.contactPersonName
                    }
                    onChange={handleChange}
                  />
                )}
              </div>

              <div className="userInformation">
                <p className="m-0">Email</p>
                {loading ? (
                  <Skeleton width="100%" height={40} />
                ) : (
                  <input
                    type="email"
                    name="email"
                    placeholder="Email"
                    value={profileData.email}
                    disabled
                  />
                )}
              </div>
            </div>

            <div className="userInformation">
              <p className="m-0">DOB</p>
              {loading ? (
                <Skeleton width="100%" height={40} />
              ) : (
                <DatePicker
                  selected={dobDate}
                  onChange={handleDateChange}
                  dateFormat="yyyy-MM-dd"
                  className="form-control"
                  placeholderText="Select DOB"
                  maxDate={
                    new Date(
                      new Date().setFullYear(new Date().getFullYear() - 10),
                    )
                  } // up to 10 years ago
                  filterDate={(date) =>
                    date <=
                    new Date(
                      new Date().setFullYear(new Date().getFullYear() - 10),
                    )
                  }
                />
              )}
            </div>

            <div>
              <button
                className="actionButton profileSaveButton"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                ) : null}
                {saving ? "Saving..." : "Save change"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
